import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CUSTOMER_SUCCESS_EMAIL,
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_FAILED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
  toastMsg,
} from "config";
import { status as StatusEnum } from "store/types/enum";
import {
  getProjectFeatureStatus,
  handleToast,
  mapProjectFeatureStatusesToFeatureStatus,
} from "utils";
import { getBudgetRerunAvailability } from "utils/aiCreditsAvailability";
import { aiRunFailureReason, registerForegroundAiWatcher } from "utils/aiRunTransitions";
import { sessionKeys } from "queries/profile.keys";
import { projectKeys } from "queries/project.keys";
import { invalidateBudgetQueries } from "queries/invalidateBudgetQueries";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { useSessionUser } from "hooks/useSession";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import {
  resolveBudgetAiRunState,
  resolveEffectiveBudgetFeatureStatus,
} from "components/dashboard/body/projects/panels/budget/budgetAiRunState";
import { targetedRerunDoneMessage } from "components/dashboard/body/projects/panels/budget/targetedRerun";
import { clearTargetedRerunCategories, takeTargetedRerunCategories } from "./targetedRerunNames";
import {
  type AcceptedBudgetRun,
  acceptedRunEnd,
  claimBudgetRunEnd,
  finishAcceptedBudgetRun,
  markAcceptedBudgetRunChecked,
  useAcceptedBudgetRun,
} from "./acceptedBudgetRun";
import { getProjectDetailQueryKey } from "hooks/useProjects/projectDetailQuery";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "analytics";

type UseBudgetAiRunSyncOptions = {
  showToasts?: boolean;
};

const getBudgetFeature = (project: ProjectState | null | undefined, projectId: string | number) =>
  getProjectFeatureStatus(
    mapProjectFeatureStatusesToFeatureStatus(
      project?.featureStatuses,
      project?.projectId ?? projectId ?? 0
    ),
    "budget"
  );

/**
 * Polls budget feature status while AI is in flight and, on COMPLETED/FAILED,
 * invalidates project + budget queries so every budget surface refreshes.
 *
 * Mount once under the budget route shell (`BudgetAiSyncProvider`).
 */
export const useBudgetAiRunSync = (options?: UseBudgetAiRunSyncOptions) => {
  const showToasts = options?.showToasts !== false;
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();
  const { isOneTimePurchase } = useSessionUser();
  const { projectId, documentId, hasValidIds } = useProjectIdentifiers(false);
  // The budget's own document status (a selected version may not be the active one). With no
  // document this is the project status.
  const { project: seedProject } = useProjectIdentifiers(false, documentId);

  // Suppress the global AI-run watcher's toasts while this sync is mounted.
  useEffect(() => {
    if (!hasValidIds || !projectId) return;
    return registerForegroundAiWatcher(projectId, ["budget", "tax_incentives"]);
  }, [hasValidIds, projectId]);

  const hasBudgetFeature = seedProject?.features?.hasBudget ?? false;
  const seedBudgetFeature = getBudgetFeature(seedProject, projectId);
  const shouldPoll = useMemo(
    () =>
      resolveBudgetAiRunState({
        hasBudgetFeature,
        hasValidIds,
        hasAnyFeatureStatuses: Boolean(seedProject?.featureStatuses?.length),
        hasBudgetStatus: Boolean(seedBudgetFeature),
        status: seedBudgetFeature?.status,
        metadata: seedBudgetFeature?.metadata,
      }).shouldPollProjectStatus,
    [
      hasBudgetFeature,
      hasValidIds,
      seedProject?.featureStatuses?.length,
      seedBudgetFeature,
      seedBudgetFeature?.status,
      seedBudgetFeature?.metadata,
    ]
  );

  // The rerun this client started, until its own end is handled. Polling continues while it is
  // open, even when the cached row still reads "completed" from an older run.
  const acceptedRun = useAcceptedBudgetRun(projectId, documentId);
  const { project: polledProject } = useProjectIdentifiers(
    shouldPoll || Boolean(acceptedRun && !acceptedRun.expired),
    documentId
  );
  const currentProject = polledProject ?? seedProject;
  const currentBudgetFeature = getBudgetFeature(currentProject, projectId);
  const budgetRunState = resolveBudgetAiRunState({
    hasBudgetFeature: currentProject?.features?.hasBudget ?? hasBudgetFeature,
    hasValidIds,
    hasAnyFeatureStatuses: Boolean(currentProject?.featureStatuses?.length),
    hasBudgetStatus: Boolean(currentBudgetFeature),
    status: currentBudgetFeature?.status,
    metadata: currentBudgetFeature?.metadata,
  });
  const { isBudgetAiRunning } = budgetRunState;
  const isBudgetRerunning =
    resolveEffectiveBudgetFeatureStatus(
      currentBudgetFeature?.status,
      currentBudgetFeature?.metadata
    ) === FEATURE_STATUS_RERUNNING;

  // Per document: the status last seen and which document it was for. A new document (or the
  // first evaluation with resolved ids) starts fresh, like a first mount.
  const previousStatusRef = useRef<string | undefined>(undefined);
  const evaluatedScopeRef = useRef<string | null>(null);

  // On expiry, fetch the document's status first; the main effect then evaluates that row once.
  const expiryCheckedRunRef = useRef<AcceptedBudgetRun | null>(null);
  useEffect(() => {
    if (!acceptedRun?.expired || acceptedRun.checked || !projectId) return;
    if (expiryCheckedRunRef.current === acceptedRun) return;
    expiryCheckedRunRef.current = acceptedRun;
    const run = acceptedRun;
    void queryClient
      .refetchQueries({ queryKey: getProjectDetailQueryKey(String(projectId), documentId) })
      .finally(() => markAcceptedBudgetRunChecked(projectId, documentId, run));
  }, [acceptedRun, projectId, documentId, queryClient]);

  useEffect(() => {
    const featureStatus = resolveEffectiveBudgetFeatureStatus(
      currentBudgetFeature?.status,
      currentBudgetFeature?.metadata
    );
    const metadata = currentBudgetFeature?.metadata;
    if (!hasValidIds || !projectId || !documentId) return;
    const scope = `${projectId}:${documentId}`;
    // Another document's status (or none yet) is never a transition for this one.
    const previousStatus =
      evaluatedScopeRef.current === scope ? previousStatusRef.current : featureStatus;
    evaluatedScopeRef.current = scope;
    previousStatusRef.current = featureStatus;

    const isTerminal =
      featureStatus === FEATURE_STATUS_COMPLETED || featureStatus === FEATURE_STATUS_FAILED;
    let wasRerunning = previousStatus === FEATURE_STATUS_RERUNNING;
    let wasInFlight = wasRerunning || previousStatus === FEATURE_STATUS_PENDING;

    if (acceptedRun) {
      // While this client's run is open, only a terminal row of its own ends it (matched by run
      // id), or a newer run's end. It may already be cached when the accept reply lands or when
      // this view mounts, and no "rerunning" row needs to have been seen.
      const end = acceptedRunEnd(acceptedRun, featureStatus, metadata);
      if (!end) {
        // Expired and fetched again with no end of its own: not a finish. Refresh and close.
        if (acceptedRun.checked) {
          finishAcceptedBudgetRun(projectId, documentId);
          void invalidateBudgetQueries(queryClient, projectId, documentId);
        }
        return;
      }
      // Someone else's newer run replaced ours: never name our categories for it.
      if (end === "newer") clearTargetedRerunCategories(projectId, documentId);
      wasRerunning = true;
      wasInFlight = true;
    }

    if (!wasInFlight || !isTerminal) return;
    // One toast per run, shared with the global watcher: whoever claims it first toasts.
    if (!claimBudgetRunEnd(projectId, documentId, featureStatus, metadata)) {
      clearTargetedRerunCategories(projectId, documentId);
      void invalidateBudgetQueries(queryClient, projectId, documentId);
      return;
    }

    if (wasInFlight) {
      if (featureStatus === FEATURE_STATUS_COMPLETED) {
        const targetedCategories = takeTargetedRerunCategories(projectId, documentId);
        if (showToasts) {
          handleToast(
            !wasRerunning
              ? toastMsg.reRunBudgeting.success.budgetReadySuccessfully
              : targetedCategories
                ? targetedRerunDoneMessage(targetedCategories)
                : toastMsg.reRunBudgeting.success.rebudgetFinishedSuccessfully,
            StatusEnum.SUCCESS
          );
        }
        queryClient.invalidateQueries({ queryKey: sessionKeys.all });
        queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
        void invalidateBudgetQueries(queryClient, projectId, documentId);
        trackEvent(ANALYTICS_EVENTS.AI_RESULT_REVIEWED, {
          project_id: String(projectId),
          document_id: String(documentId),
          output_type: "budget",
          first_value_type: "budget_reviewed",
        });
      } else if (featureStatus === FEATURE_STATUS_FAILED) {
        if (showToasts) {
          // Say why it failed when the backend sent a reason; the old wording is the fallback.
          const reason = aiRunFailureReason(currentBudgetFeature?.metadata);
          if (!wasRerunning) {
            handleToast(
              `Budget generation failed. ${reason ?? `Please try again or contact ${CUSTOMER_SUCCESS_EMAIL}.`}`,
              StatusEnum.ERROR
            );
          } else if (isOneTimePurchase) {
            handleToast(
              `Rebudget failed. ${reason ?? `Please try again or contact ${CUSTOMER_SUCCESS_EMAIL}.`}`,
              StatusEnum.ERROR
            );
          } else {
            const avail = getBudgetRerunAvailability(currentProject ?? undefined);
            const rerunsLeft = avail.remainingUnits;
            const creditNote =
              rerunsLeft > 0
                ? `You still have ${rerunsLeft} AI credit unit(s) left (cost ${avail.cost} per run).`
                : "You have no AI credits left for this action.";
            const errorMessage = reason
              ? `Rebudget failed. ${reason} ${creditNote}`
              : rerunsLeft > 0
                ? `Rebudget failed. ${creditNote} Please try again!`
                : `Rebudget failed. ${creditNote}`;
            handleToast(errorMessage, StatusEnum.ERROR);
          }
        }
        clearTargetedRerunCategories(projectId, documentId);
        queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
        void invalidateBudgetQueries(queryClient, projectId, documentId);
      }
    }
  }, [
    currentBudgetFeature?.status,
    currentBudgetFeature?.metadata,
    acceptedRun,
    projectId,
    documentId,
    hasValidIds,
    currentProject,
    queryClient,
    showToasts,
    isOneTimePurchase,
    trackEvent,
  ]);

  return {
    currentProject,
    currentBudgetFeature,
    isBudgetAiRunning,
    isBudgetRerunning,
    projectId,
    documentId,
    hasValidIds,
  };
};
